import { ReactNode, useMemo, useCallback, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm, UseFormReturn } from "react-hook-form";
import { ListingWizardProvider } from "@/context/ListingWizardContext";
import { ListingFormProvider } from "@/context/ListingFormContext";
import {
  FormStep,
  ListingFormValues,
} from "@/components/listings/filters/FilterSchema";
import { useFormState } from "./hooks/useFormState";
import { useFormValidation } from "./hooks/useFormValidation";
import { useFormChanges } from "./hooks/useFormChanges";
import { useFormInitialization } from "./hooks/useFormInitialization";
import { useListingWizard } from "@/hooks/useListingWizard";
import { Listing } from "@/lib/types";
import {
  mapFormToApiData,
  finalReviewSchema,
  mapListingToFormValues,
} from "./utils/formUtils";
import { toast } from "sonner";
import { ZodError } from "zod";
import { listingWizardService } from "@/lib/api/listingWizardService";
import { usePropertyTypesContext } from "@/context/PropertyTypesContext";

interface FormProviderProps {
  children: ReactNode;
}

const FormProvider = ({ children }: FormProviderProps) => {
  const params = useParams<{ listingId?: string }>();

  const loadFormDataFromLocalStorage = useCallback(() => {
    const data = localStorage.getItem("listingFormData");
    if (data) {
      return JSON.parse(data) as ListingFormValues;
    }
    return undefined;
  }, []);

  const form = useFormInitialization(loadFormDataFromLocalStorage);

  const [listingId, setListingId] = useState<number | undefined>(() => {
    const initialId = params.listingId ? Number(params.listingId) : undefined;
    console.log("[FormProvider] Initializing listingId state:", initialId);
    return initialId;
  });

  const [isInitialLoadFromParams, setIsInitialLoadFromParams] =
    useState<boolean>(!!params.listingId);
  const [isCreatingDraft, setIsCreatingDraft] = useState<boolean>(false);
  const [
    isFormInitializedWithFetchedData,
    setIsFormInitializedWithFetchedData,
  ] = useState<boolean>(false);
  const [isFormResetCompleted, setIsFormResetCompleted] = useState(false); // New state

  const {
    createDraftMutation,
    updateDetailsMutation,
    updateLocationMutation,
    updateContactMutation,
    updateTitleDescMutation,
    publishListingMutation,
    useDraftListing,
  } = useListingWizard();

  const { propertyTypeMap, isLoading: arePropertyTypesLoading } =
    usePropertyTypesContext();

  console.log("[FormProvider] PropertyTypesContext values:", {
    arePropertyTypesLoading,
    propertyTypeMapSize: Object.keys(propertyTypeMap).length,
  });

  const { data: fetchedDraftListing } = useDraftListing(listingId);

  const {
    currentStep,
    setCurrentStep,
    isSubmitting,
    setIsSubmitting,
    completedSteps,
    setStepCompleted,
    canProceedToNextStep,
    setCanProceedToNextStep,
    completionPercentage,
    formTouched, // formTouched is now derived, no setter needed
    validateOnChange,
    setValidateOnChange,
  } = useFormState();

  // Main useEffect for form initialization and data population
  useEffect(() => {
    console.log("[FormProvider] --- useEffect Start ---");
    console.log(
      "[FormProvider] Main useEffect dependencies changed. Current states:",
      {
        fetchedDraftListing: !!fetchedDraftListing,
        arePropertyTypesLoading,
        propertyTypeMapLoaded: Object.keys(propertyTypeMap).length > 0,
        listingId,
        isFormResetCompleted,
      }
    );
    console.log("[FormProvider] Raw fetchedDraftListing:", fetchedDraftListing);

    // Condition to proceed with form reset
    const allDataReady = fetchedDraftListing && !arePropertyTypesLoading;

    if (allDataReady && !isFormResetCompleted) {
      console.log(
        "[FormProvider] All data ready and form not yet reset. Proceeding with form reset."
      );
      console.log(
        "[FormProvider] fetchedDraftListing.images before mapping:",
        fetchedDraftListing?.images
      ); // Add this log

      const savedLocalData = loadFormDataFromLocalStorage();
      console.log("[FormProvider] Saved local data:", savedLocalData);

      console.log(
        "[FormProvider] Calling mapListingToFormValues with:",
        fetchedDraftListing
      );
      const mappedListingData = mapListingToFormValues(fetchedDraftListing);
      console.log("[FormProvider] Mapped listing data:", mappedListingData);

      const resetData: ListingFormValues = {
        ...mappedListingData,
        ...savedLocalData,
        location: {
          ...mappedListingData.location,
          ...savedLocalData?.location,
        },
        contactDetails: {
          ...mappedListingData.contactDetails,
          ...savedLocalData?.contactDetails,
        },
        images: fetchedDraftListing.images || [],
        videoUrl: fetchedDraftListing.listing?.video_url || "", // Corrected
      };

      if (
        fetchedDraftListing.listing?.property_type_id && // Corrected
        !arePropertyTypesLoading &&
        propertyTypeMap[fetchedDraftListing.listing.property_type_id.toString()] // Corrected
      ) {
        resetData.propertyTypeDisplayName =
          propertyTypeMap[
            fetchedDraftListing.listing.property_type_id.toString()
          ]; // Corrected
      } else if (savedLocalData?.propertyTypeDisplayName) {
        resetData.propertyTypeDisplayName =
          savedLocalData.propertyTypeDisplayName;
      }

      console.log("[FormProvider] Merged data for form reset:", resetData);

      const updateAndResetForm = async () => {
        setIsFormInitializedWithFetchedData(false);
        if (
          fetchedDraftListing.listing?.city_id && // Corrected
          typeof fetchedDraftListing.listing.city_id === "number" // Corrected
        ) {
          try {
            const city = await listingWizardService.getCityById(
              fetchedDraftListing.listing.city_id // Corrected
            );
            if (city && typeof city.district_id === "number") {
              const district = await listingWizardService.getDistrictById(
                city.district_id
              );
              if (district && typeof district.province_id === "number") {
                const provinces = await listingWizardService.getProvinces();
                const province = provinces.find(
                  (p) => p.id === district.province_id
                );

                // Populate all location fields in resetData before form.reset
                resetData.location = {
                  ...resetData.location, // Keep existing address, lat, long
                  provinceId: district.province_id, // Set province ID
                  districtId: city.district_id, // Set district ID
                  city_id: city.id, // Set city ID (already there, but explicit)
                  province: province?.name || "",
                  district: district?.name || "",
                  city: city.name || "",
                };
              } else {
                console.warn(
                  "[FormProvider] District or province_id not found for city:",
                  city
                );
              }
            } else {
              console.warn(
                "[FormProvider] City or district_id not found for fetchedDraftListing.listing.city_id:", // Corrected
                fetchedDraftListing.listing.city_id // Corrected
              );
            }
          } catch (error) {
            console.error(
              "Error fetching location details for form reset:",
              error
            );
          }
        }
        console.log(
          "[FormProvider] Final data before form.reset():",
          resetData
        );
        form.reset(resetData);
        setIsFormInitializedWithFetchedData(true);
        setIsFormResetCompleted(true); // Set to true after successful reset

        let determinedStep = FormStep.PropertyInformation;

        if (isInitialLoadFromParams) {
          if (
            fetchedDraftListing.listing?.title && // Corrected
            fetchedDraftListing.listing?.description && // Corrected
            (fetchedDraftListing.listing.title !== "Draft Listing" || // Corrected
              fetchedDraftListing.listing.description !==
                "Draft listing description") // Corrected
          ) {
            determinedStep = FormStep.Review;
          } else if (
            fetchedDraftListing.listing?.contact_name || // Corrected
            fetchedDraftListing.listing?.contact_phone || // Corrected
            fetchedDraftListing.listing?.contact_email // Corrected
          ) {
            determinedStep = FormStep.TitleDescription;
          } else if (
            fetchedDraftListing.images &&
            fetchedDraftListing.images.length > 0
          ) {
            determinedStep = FormStep.Contact;
          } else if (
            fetchedDraftListing.listing?.address || // Corrected
            fetchedDraftListing.listing?.city_id // Corrected
          ) {
            determinedStep = FormStep.Media;
          } else if (
            fetchedDraftListing.listing?.size || // Corrected
            fetchedDraftListing.listing?.bedrooms || // Corrected
            fetchedDraftListing.listing?.bathrooms // Corrected
          ) {
            determinedStep = FormStep.Location;
          } else if (
            fetchedDraftListing.listing?.property_type_id && // Corrected
            fetchedDraftListing.listing?.listing_type && // Corrected
            fetchedDraftListing.listing?.price // Corrected
          ) {
            determinedStep = FormStep.PropertyDetails;
          }

          console.log(
            "[FormProvider] Determined starting step for existing draft (from URL):",
            FormStep[determinedStep]
          );
          setCurrentStep(determinedStep);
          for (let i = 0; i < determinedStep; i++) {
            setStepCompleted(i as FormStep, true);
          }
        } else {
          if (
            fetchedDraftListing.listing?.address ||
            fetchedDraftListing.listing?.city_id
          ) {
            // Corrected
            determinedStep = FormStep.Media;
            setCurrentStep(determinedStep);
            setStepCompleted(FormStep.Location, true);
            console.log(
              "[FormProvider] Determined starting step for new draft (after initial save):",
              FormStep[determinedStep]
            );
          } else {
            determinedStep = currentStep;
            setCurrentStep(determinedStep);
            console.log(
              "[FormProvider] Determined starting step for new draft (no initial save yet):",
              FormStep[determinedStep]
            );
          }
        }
        console.log("[FormProvider] --- updateAndResetForm End ---");
      };

      updateAndResetForm();
      console.log("[FormProvider] --- useEffect End (Executed) ---");
    } else {
      console.log(
        "[FormProvider] Skipping form reset. Conditions not met or already reset.",
        { allDataReady, isFormResetCompleted }
      );
      console.log("[FormProvider] --- useEffect End (Skipped) ---");
    }
  }, [
    fetchedDraftListing,
    form,
    setCurrentStep,
    setStepCompleted,
    isInitialLoadFromParams,
    currentStep,
    loadFormDataFromLocalStorage,
    arePropertyTypesLoading,
    propertyTypeMap,
    listingId,
    isFormResetCompleted,
  ]);

  useEffect(() => {
    if (params.listingId && Number(params.listingId) !== listingId) {
      const idFromParams = Number(params.listingId);
      console.log(
        "[FormProvider] Setting listingId from URL params:",
        idFromParams
      );
      setListingId(idFromParams);
      console.log(
        "[FormProvider] listingId state after URL param update:",
        idFromParams
      );
    }
  }, [params.listingId, listingId]);

  const memoizedSetCurrentStep = useCallback(
    (step: FormStep) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );

  const memoizedSetIsSubmitting = useCallback(
    (submitting: boolean) => {
      setIsSubmitting(submitting);
    },
    [setIsSubmitting]
  );

  const memoizedSetStepCompleted = useCallback(
    (step: FormStep, completed: boolean) => {
      setStepCompleted(step, completed);
    },
    [setStepCompleted]
  );

  const memoizedSetCanProceedToNextStep = useCallback(
    (canProceed: boolean) => {
      setCanProceedToNextStep(canProceed);
    },
    [setCanProceedToNextStep]
  );

  const memoizedSetValidateOnChange = useCallback(
    (validate: boolean) => {
      setValidateOnChange(validate);
    },
    [setValidateOnChange]
  );

  const saveFormDataToLocalStorage = useCallback(
    (values: ListingFormValues) => {
      localStorage.setItem("listingFormData", JSON.stringify(values));
    },
    []
  );

  const clearAllListingData = useCallback(() => {
    localStorage.removeItem("listingFormData");
    localStorage.removeItem("listingImageUrls");
    console.log(
      "[FormProvider] Cleared listing form data and image URLs from local storage."
    );
  }, []);

  const saveCurrentStep = useCallback(async (): Promise<
    Listing | undefined
  > => {
    console.log(
      "[FormProvider] saveCurrentStep: Entered. Checking conditions.",
      { formExists: !!form, isSubmitting }
    );
    if (!form || isSubmitting) {
      console.log("[FormProvider] saveCurrentStep: Exiting early.", {
        formExists: !!form,
        isSubmitting,
      });
      return fetchedDraftListing;
    }

    try {
      setIsSubmitting(true);
      const values = form.getValues();
      console.log("[FormProvider] saveCurrentStep - Form Values:", values);
      console.log(
        "[FormProvider] saveCurrentStep - Current Step:",
        FormStep[currentStep]
      );
      console.log(
        "[FormProvider] saveCurrentStep - Existing Listing ID (from state):",
        listingId
      );

      console.log(
        "[FormProvider] saveCurrentStep - Saving to local storage for step:",
        FormStep[currentStep]
      );
      saveFormDataToLocalStorage(values);
      toast.success("Progress saved to local storage");

      const apiData = mapFormToApiData(values);
      console.log("[FormProvider] saveCurrentStep - Mapped API Data:", apiData);

      const currentListingId = listingId || (form.getValues() as any).id;
      const stepListingId = currentListingId;

      if (!stepListingId && currentStep >= FormStep.Media) {
        console.error(
          "[FormProvider] saveCurrentStep - No listing ID available for step:",
          FormStep[currentStep]
        );
        throw new Error("No listing ID available to save step data.");
      }

      if (currentStep === FormStep.Location) {
        console.log(
          "[FormProvider] saveCurrentStep - Saving Location data (first backend save)."
        );
        if (!stepListingId) {
          if (isCreatingDraft) {
            console.log(
              "[FormProvider] Draft creation already in progress. Exiting to prevent duplicate creation."
            );
            return fetchedDraftListing;
          }
          setIsCreatingDraft(true);
          try {
            console.log(
              "[FormProvider] saveCurrentStep - Creating new draft with all collected data (Location Step)..."
            );
            console.log(
              "[FormProvider] Calling createDraftMutation with data:",
              apiData
            );
            const createResult = await createDraftMutation.mutateAsync(apiData);
            console.log(
              "[FormProvider] saveCurrentStep - Create Draft Result (Location Step):",
              createResult
            );

            if (
              createResult &&
              createResult.listing &&
              createResult.listing.id
            ) {
              const newListingId = Number(createResult.listing.id);
              console.log(
                "[FormProvider] saveCurrentStep - New Listing ID from Create (Location Step):",
                newListingId
              );
              setListingId(newListingId);
              if (createResult.listing.title) {
                form.setValue("title", createResult.listing.title);
              }
              if (createResult.listing.description) {
                form.setValue("description", createResult.listing.description);
              }
              console.log(
                "[FormProvider] listingId state after setListingId (new):",
                newListingId
              );
              console.log(
                "[FormProvider] saveCurrentStep - New draft created with full data. Subsequent updates will be handled by other steps."
              );
              return createResult; // createResult is now of type Listing
            } else {
              console.error(
                "[FormProvider] saveCurrentStep - Draft creation failed or did not return an ID (Location Step).",
                createResult
              );
              throw new Error(
                "Failed to create draft listing. Please try again."
              );
            }
          } finally {
            setIsCreatingDraft(false);
          }
        } else {
          console.log(
            "[FormProvider] saveCurrentStep - Listing ID already exists. Skipping backend update for Location Step. Proceeding to next step."
          );
          return fetchedDraftListing;
        }
      }

      if (currentStep === FormStep.Media) {
        console.log(
          "[FormProvider] saveCurrentStep - Marking Media step as completed for navigation."
        );
        setStepCompleted(currentStep, true);
        console.log(
          "[FormProvider] completedSteps after marking Media:",
          completedSteps
        );
      }

      if (currentStep === FormStep.Contact) {
        console.log(
          "[FormProvider] saveCurrentStep - Contact step: Data saved to local storage only."
        );
      }

      if (currentStep === FormStep.TitleDescription) {
        console.log(
          "[FormProvider] saveCurrentStep - Title & Description step: Data saved to local storage only."
        );
      }

      setStepCompleted(currentStep, true);
      toast.success("Progress saved successfully");
      return fetchedDraftListing; // fetchedDraftListing is already of type Listing
    } catch (error) {
      console.error("[FormProvider] Error saving current step:", error);
      toast.error("Failed to save progress. Please try again.");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    currentStep,
    listingId,
    isSubmitting,
    createDraftMutation,
    updateDetailsMutation,
    updateLocationMutation,
    updateContactMutation,
    updateTitleDescMutation,
    setStepCompleted,
    setIsSubmitting,
    saveFormDataToLocalStorage,
    fetchedDraftListing,
  ]);

  const publishListing = useCallback(async () => {
    if (!listingId) {
      toast.error("Cannot publish: No listing ID found.");
      return;
    }
    if (!form) {
      toast.error("Cannot publish: Form not available.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formValues = form.getValues();
      const apiData = mapFormToApiData(formValues);

      console.log("[FormProvider] Data for final review validation:", apiData);
      const validationResult = finalReviewSchema.safeParse(apiData);

      if (!validationResult.success) {
        const zodError = validationResult.error as ZodError;
        console.error(
          "[FormProvider] Final review validation failed:",
          zodError.flatten().fieldErrors
        );

        let errorMessages = "Validation failed: ";
        const fieldErrors = zodError.flatten().fieldErrors;
        const messages = Object.entries(fieldErrors).map(([field, msgs]) => {
          let userFriendlyField = field.replace(/_/g, " ");
          userFriendlyField =
            userFriendlyField.charAt(0).toUpperCase() +
            userFriendlyField.slice(1);
          return `${userFriendlyField}: ${msgs?.join(", ")}`;
        });

        errorMessages += messages.join("; ");
        toast.error(errorMessages, { duration: 10000 });
        setIsSubmitting(false);
        return;
      }

      console.log(
        "[FormProvider] Final review validation passed. Publishing..."
      );
      console.log(
        "[FormProvider] Calling publishListingMutation with listingId:",
        listingId,
        "and data:",
        apiData
      );
      const result = await publishListingMutation.mutateAsync({
        listingId,
        data: apiData,
      });

      if (result) {
        toast.success("Listing published successfully!");
        localStorage.removeItem("listingFormData");
        localStorage.removeItem("listingImageUrls");
      }
      return result;
    } catch (error: any) {
      console.error("[FormProvider] Error publishing listing:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to publish listing. Please try again.";
      if (
        !(error instanceof ZodError) &&
        !String(errorMessage).toLowerCase().includes("validation failed")
      ) {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [listingId, form, publishListingMutation, setIsSubmitting]);

  // setFormTouched is no longer passed as it's derived in useFormState
  useFormChanges(form, formTouched, validateOnChange);

  useFormValidation(
    form,
    currentStep,
    validateOnChange,
    memoizedSetCanProceedToNextStep,
    memoizedSetStepCompleted,
    fetchedDraftListing,
    isFormInitializedWithFetchedData // Pass the new state
  );

  const contextValue = useMemo(
    () => ({
      form,
      currentStep,
      setCurrentStep: memoizedSetCurrentStep,
      listingId,
      setListingId,
      draftListing: fetchedDraftListing,
      setDraftListing: () => {
        console.warn(
          "setDraftListing called, but draftListing is now managed by React Query. This function is a no-op."
        );
      },
      isSubmitting,
      setIsSubmitting: memoizedSetIsSubmitting,
      completedSteps,
      setStepCompleted: memoizedSetStepCompleted,
      canProceedToNextStep,
      setCanProceedToNextStep: memoizedSetCanProceedToNextStep,
      completionPercentage,
      formTouched,
      // setFormTouched is no longer needed as formTouched is derived
      validateOnChange,
      setValidateOnChange: memoizedSetValidateOnChange, // Re-add this line
      saveCurrentStep,
      publishListing,
      saveFormDataToLocalStorage,
      loadFormDataFromLocalStorage,
      clearAllListingData, // Expose the new function
    }),
    [
      form,
      currentStep,
      memoizedSetCurrentStep,
      listingId,
      setListingId,
      fetchedDraftListing,
      isSubmitting,
      memoizedSetIsSubmitting,
      completedSteps,
      memoizedSetStepCompleted,
      canProceedToNextStep,
      memoizedSetCanProceedToNextStep,
      completionPercentage,
      formTouched,
      validateOnChange,
      memoizedSetValidateOnChange,
      saveCurrentStep,
      publishListing,
      saveFormDataToLocalStorage,
      loadFormDataFromLocalStorage, // Add this back to dependencies
      clearAllListingData, // Add this back to dependencies
    ]
  );

  return (
    <ListingWizardProvider value={contextValue}>
      <ListingFormProvider value={contextValue}>{children}</ListingFormProvider>
    </ListingWizardProvider>
  );
};

export default FormProvider;
